import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDecryptMetadataMutation, type DecryptMetadataResponse } from '@/store/api/decryptAndValidateApi';
import { useAddEventLogMutation } from '@/store/api/eventsApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setContactId, setMetadata, setMetadataProcessed } from '@/store/reducers/authSlice';
import { PAGE_TYPE } from '../constants/segmentConstants';
import { useAnalytics } from '../hooks/useAnalytics';
import Loader from '../ui/Loader';

const REQUIRED_KEYS = ['contactId'];

interface MetadataHandlerProps {
    children: ReactNode;
}

export const MetadataHandler = ({ children }: MetadataHandlerProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [decryptMetadata] = useDecryptMetadataMutation();
    const [addEventLog] = useAddEventLogMutation();
    const { identifyUser } = useAnalytics({page_type: PAGE_TYPE.ILENDING_HUB_UPLOAD});
    const ranRef = useRef(false);
    const [isProcessing, setIsProcessing] = useState(true);
    const authToken = useAppSelector((state) => state.auth.token);

    useEffect(() => {
        if (!authToken) {
            return;
        }
        if (ranRef.current) return;
        ranRef.current = true;

        const urlParams = new URLSearchParams(location.search);
        const shouldNavigateToProducts =
            urlParams.has('products') ||
            urlParams.get('products') === 'true';

        const targetRoute = shouldNavigateToProducts
            ? '/products'
            : '/home';
        const metaString = urlParams.get('metadata');

        if (metaString) {
            decryptMetadata({ metaString })
                .unwrap()
                .then((data: DecryptMetadataResponse) => {
                    const hasAll = REQUIRED_KEYS.every(
                        (key) => data[key as keyof DecryptMetadataResponse]
                    );

                    if (!hasAll) {
                        navigate(`/error?${urlParams.toString()}`);
                        return;
                    }

                    dispatch(setMetadata(data));
                    dispatch(setMetadataProcessed(true));
        
                    if (data.contactId) {
                        identifyUser(data.contactId);
                        dispatch(setContactId(data.contactId));
                    }

                    setIsProcessing(false);
                    navigate(`${targetRoute}${location.search}`);
                })
                .catch(() => {
                    setIsProcessing(false);
                    navigate(`/error?${urlParams.toString()}`);
                });
        } else {
            dispatch(setMetadataProcessed(true));
            setIsProcessing(false);
            navigate(`${targetRoute}${location.search}`);
        }

    }, [location.search, decryptMetadata, dispatch, navigate, addEventLog]);

    if (isProcessing) {
        return (
            <div className="home__loading">
                <Loader />
            </div>
        );
    }
    return <>{children}</>;
};
